'use client';

import React, { useState, useEffect, ReactNode, useRef } from 'react';
import styles from './onlineRateTable.module.scss';
import classNames from 'classnames';
import { useLocalStorage } from '../../../hooks/useLocalStorage/useLocalStorage';
import CurrencyDropdown from '../../CurrencyConverter/currencySelector/currencyDropdown/currencyDropdown';
import { RemoveCustomRate } from '../RemoveCustomRate/RemoveCustomRate';
import { ParsedRate, ParsedRateData } from '../../../utils/getRates/getRates';

export type OnlineRateTableContainerProps = {
  currencyColumnLabel: string;
  sellRateColumnLabel: string;
  callToAction?: ReactNode;
  rateData: ParsedRateData;
  footer?: ReactNode;
  disableAddingViewingCustomCurrencies: boolean;
  customCurrencies?: string[];
  dataToDisplay?: string;
  rateSaleAllCurrencies: boolean;
  osakaChangesEnabled?: boolean;
};

const getCurrencyCodes = (rateList: any) => {
  return rateList.map((rate: any) => rate.currencyCode);
};

type AddCurrencyProps = {
  rates: any[];
  addRateHandler: (event: MouseEvent | TouchEvent) => void;
  setShowAddCurrency: React.Dispatch<React.SetStateAction<boolean>>;
  showAddCurrency: boolean;
  dropdownWrapperRef: React.RefObject<HTMLDivElement | null>;
};

const AddCurrency = ({
  rates,
  addRateHandler,
  setShowAddCurrency,
  showAddCurrency,
  dropdownWrapperRef,
}: AddCurrencyProps) => {
  if (rates.length === 0) return null;

  const focusFirstDropdownItem = () => {
    setTimeout(() => {
      const dropdownContainer = dropdownWrapperRef.current?.querySelector(
        '.currencyDropdownContainer, [class*="dropdown"]'
      );
      const firstDropdownItem = dropdownContainer?.querySelector(
        'button, [role="option"], [tabindex="0"], a, input, select, textarea, li[data-item-index="0"]'
      );
      if (firstDropdownItem) {
        (firstDropdownItem as HTMLElement).focus();
      }
    }, 50);
  };

  const handleToggleDropdown = () => {
    const newShowState = !showAddCurrency;
    setShowAddCurrency(newShowState);

    if (newShowState) {
      focusFirstDropdownItem();
    }
  };

  return (
    <div ref={dropdownWrapperRef} className={classNames(styles.dropdownAnchor)}>
      <div className={styles.addCurrencyRow}>
        <div className={styles.addCurrencyContent}>
          <button
            className={styles.addCurrencyButton}
            onClick={handleToggleDropdown}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleDropdown();
              }
            }}
            aria-expanded={showAddCurrency}
            aria-haspopup="listbox"
            aria-label="Add currency to rate table"
          >
            <img src="/icons/add.svg" aria-hidden="true" alt="" />
            <span>Add currency</span>
          </button>
        </div>
        {showAddCurrency && (
          <div className={styles.currencyDropdownContainer}>
            <CurrencyDropdown
              currencyList={rates}
              handleListItemClick={addRateHandler}
              setDropdownIsOpen={setShowAddCurrency}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const TableRow = ({
  currency,
  sellRateColumnLabel,
  removeRateHandler = undefined,
  disableAddingViewingCustomCurrencies,
  dataToDisplay = 'Online Sell Rate Data',
  rateSaleAllCurrencies,
  osakaChangesEnabled = false,
}: {
  currency: any;
  sellRateColumnLabel: string;
  removeRateHandler?: any;
  disableAddingViewingCustomCurrencies: boolean;
  dataToDisplay?: string;
  rateSaleAllCurrencies: boolean;
  osakaChangesEnabled: boolean;
}) => {
  return (
    <div
      className={classNames(
        styles.row,
        disableAddingViewingCustomCurrencies ? styles.roundBottom : null
      )}
      role="row"
    >
      <div role="cell" className={styles.currencyNameWrapper}>
        <div className={styles.currencyName}>
          <div className={styles.flagWrapper}>
            <img
              src={`https://jl-money-static-assets-prod.storage.googleapis.com/Flags/${currency.countryCode.toLowerCase()}.svg`}
              alt={`${currency.countryName} flag`}
            />
          </div>
          <div>
            {currency.currencyName} ({currency.currencyCode})
          </div>
          {(rateSaleAllCurrencies || currency.rateSale) && (
            <div className={styles.rateSaleDesktop}>
              <img src="/icons/reward-points.svg" alt="rate sale badge" />
              <span className={styles.rateSaleText}>RATE SALE</span>
            </div>
          )}
        </div>
        <div
          className={classNames(
            styles.removeCustomRate,
            styles.removeCustomRateMobile
          )}
        >
          {removeRateHandler && (
            <RemoveCustomRate
              handleClick={() => removeRateHandler(currency.currencyCode)}
            />
          )}
        </div>
      </div>
      {(rateSaleAllCurrencies || currency.rateSale) && (
        <div role="cell" className={styles.rateSaleMobile}>
          <img src="/icons/reward-points.svg" alt="rate sale badge" />
          <span className={styles.rateSaleText}>RATE SALE</span>
        </div>
      )}
      <div role="cell" className={styles.sellRateBlock}>
        <div
          className={styles.sellRateLabel}
          aria-label={
            osakaChangesEnabled ? `${currency.currencyCode} per £1` : undefined
          }
        >
          {osakaChangesEnabled
            ? `${currency.currencyCode} per £1`
            : sellRateColumnLabel}
        </div>
        <div className={styles.sellRateValue}>
          {dataToDisplay === 'Online Sell Rate Data'
            ? currency.sellRate
            : currency.buyRate}
        </div>
      </div>
      <div
        className={classNames(
          styles.removeCustomRate,
          styles.removeCustomRateDesktop
        )}
      >
        {removeRateHandler && (
          <RemoveCustomRate
            handleClick={() => removeRateHandler(currency.currencyCode)}
          />
        )}
      </div>
    </div>
  );
};

export default function OnlineRateTableCC({
  currencyColumnLabel,
  sellRateColumnLabel,
  callToAction,
  rateData,
  footer,
  disableAddingViewingCustomCurrencies,
  customCurrencies,
  dataToDisplay = 'Online Sell Rate Data',
  rateSaleAllCurrencies,
  osakaChangesEnabled = false,
}: OnlineRateTableContainerProps) {
  const [visibleRateData, setVisibleRateData] = useState<Array<any>>([]);
  const [customRateData, setCustomRateData] = useState<Array<any>>([]);
  const [showAddCurrency, setShowAddCurrency] = useState<boolean>(false);
  const [getCustomCurrencyList, setCustomCurrencyList] =
    useLocalStorage('jlm-currencies');

  const dropdownWrapperRef = useRef<HTMLDivElement>(null);

  const filteredRates = rateData.rates.filter((currency: ParsedRate) => {
    if (
      dataToDisplay === 'Online Sell Rate Data' &&
      currency.sellRate &&
      currency.sellRate !== '0' &&
      currency.sellRate !== ''
    )
      return true;
    if (
      dataToDisplay === 'Buy Rate Data' &&
      currency.buyRate &&
      currency.buyRate !== '0.0000'
    )
      return true;
    return false;
  });

  const updateCustomCurrencies = () => {
    const customCurrencyList = getCustomCurrencyList();

    let customRate = rateData.rates.filter((rate: any) =>
      customCurrencyList.includes(rate.currencyCode)
    );

    if (visibleRateData) {
      const visibleCurrencies = visibleRateData.map(
        (item) => item.currencyCode
      );
      customRate = customRate.filter(
        (rate: any) => !visibleCurrencies.includes(rate.currencyCode)
      );
    }

    setCustomRateData(customRate);
  };

  const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
    if (
      dropdownWrapperRef.current &&
      !dropdownWrapperRef.current.contains(event.target as Node)
    ) {
      setShowAddCurrency(false);
    }
  };

  useEffect(() => {
    if (filteredRates) {
      if (customCurrencies && customCurrencies.length > 0) {
        const customRates = filteredRates.filter((rate: any) =>
          customCurrencies.includes(rate.currencyCode)
        );
        setVisibleRateData(customRates);
      } else {
        if (window.innerWidth < 960) {
          setVisibleRateData(filteredRates.slice(0, 6));
        } else {
          setVisibleRateData(filteredRates.slice(0, 12));
        }
      }
    }
    updateCustomCurrencies();

    document.addEventListener(
      'customCurrenciesChanged',
      updateCustomCurrencies
    );

    document.body.addEventListener('click', handleOutsideClick);

    return () => {
      document.body.removeEventListener('click', handleOutsideClick);
      document.removeEventListener(
        'customCurrenciesChanged',
        updateCustomCurrencies
      );
    };
  }, []);

  const allVisibleRatesCodes = [
    ...getCurrencyCodes(visibleRateData),
    ...getCurrencyCodes(customRateData),
  ];

  const remainingRates = filteredRates.filter(
    (rate: any) => !allVisibleRatesCodes.includes(rate.currencyCode)
  );

  const effectiveDateTime = rateData.effectiveDateTime.replace(' ', ', ');

  const addRateHandler = (event: MouseEvent | TouchEvent) => {
    const target = event.currentTarget as HTMLElement;
    const index = target.getAttribute('data-item-index');

    if (index === null) return;
    const numericIndex = Number(index);
    if (isNaN(numericIndex)) return;

    const newCustomRates = [...customRateData, remainingRates[numericIndex]];
    setCustomCurrencyList(JSON.stringify(getCurrencyCodes(newCustomRates)));
    setCustomRateData(newCustomRates);
    setShowAddCurrency(false);
    document.dispatchEvent(new CustomEvent('customCurrenciesChanged'));
  };

  const removeRateHandler = (currencyCodeToRemove: string) => {
    const newCustomRates = customRateData.filter(
      (rate: any) => rate.currencyCode !== currencyCodeToRemove
    );
    setCustomCurrencyList(JSON.stringify(getCurrencyCodes(newCustomRates)));
    setCustomRateData(newCustomRates);
    document.dispatchEvent(new CustomEvent('customCurrenciesChanged'));
  };

  useEffect(() => {
    updateCustomCurrencies();
  }, [visibleRateData]);

  return (
    <div role="region" aria-label="Online Rate Table">
      <div role="table" className={styles.rateTable}>
        <div role="row" className={classNames(styles.heading)}>
          <div
            data-testid="currency-column-label"
            className={styles.headerCurrency}
            role="columnheader"
          >
            {currencyColumnLabel}
          </div>
          <div
            data-testid="rate-column-label"
            role="columnheader"
            className={styles.sellRate}
          >
            {sellRateColumnLabel}
          </div>
        </div>
        <div className={styles.body} role="rowgroup">
          {visibleRateData &&
            visibleRateData.map((value, index) => {
              return (
                <TableRow
                  currency={value}
                  sellRateColumnLabel={sellRateColumnLabel}
                  key={`online-rate-table-row-${index}`}
                  disableAddingViewingCustomCurrencies={
                    disableAddingViewingCustomCurrencies &&
                    index === visibleRateData.length - 1
                  }
                  dataToDisplay={dataToDisplay}
                  rateSaleAllCurrencies={rateSaleAllCurrencies}
                  osakaChangesEnabled={osakaChangesEnabled}
                />
              );
            })}
        </div>
        {!disableAddingViewingCustomCurrencies && (
          <div role="rowgroup" className={styles.customCurrencies}>
            {customRateData &&
              customRateData.length > 0 &&
              customRateData.map((value, index) => {
                return (
                  <TableRow
                    currency={value}
                    sellRateColumnLabel={sellRateColumnLabel}
                    key={`online-rate-table-custom-row-${index}`}
                    removeRateHandler={() =>
                      removeRateHandler(value.currencyCode)
                    }
                    disableAddingViewingCustomCurrencies={
                      disableAddingViewingCustomCurrencies &&
                      index === visibleRateData.length - 1
                    }
                    dataToDisplay={dataToDisplay}
                    rateSaleAllCurrencies={rateSaleAllCurrencies}
                    osakaChangesEnabled={osakaChangesEnabled}
                  />
                );
              })}

            <AddCurrency
              rates={remainingRates}
              addRateHandler={addRateHandler}
              setShowAddCurrency={setShowAddCurrency}
              showAddCurrency={showAddCurrency}
              dropdownWrapperRef={dropdownWrapperRef}
            />
          </div>
        )}
      </div>

      <div className={styles.effectiveDateWrapper}>
        <span>Rate correct as of {effectiveDateTime}</span>
      </div>
      {callToAction && (
        <div className={styles.callToAction}>{callToAction}</div>
      )}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
